/**
 * Agregaciones derivadas de TimelineSpan[] para la vista de detalle de sesión.
 *
 * Diseñado para ser cheap (un solo pass por la lista) y stateless — todo se
 * recalcula client-side desde lo que ya devuelve `/admin/sessions/:id/timeline`.
 *
 * Notas de implementación:
 *  - Costos viajan como string nano (10^8 = $1, ver lib/format.ts). Se acumulan
 *    como bigint para no perder precisión, y se exponen también como number
 *    para chart math (los charts no necesitan precisión sub-cent).
 *  - Duraciones quedan en ms (number) — el backend ya las precalcula.
 *  - Las dimensiones soportadas hoy: `kind` (llm/stt/tts/…) y `providerId`
 *    (extraído de `attributes.provider` cuando existe; fallback 'unknown').
 *  - Para agregaciones por kind/provider DESCONTAMOS los kinds container
 *    (`session`, `turn`, `composite`) — sus costos/duraciones son sums de
 *    los hijos y romperían los totales si se incluyeran.
 */

import type { TimelineSpan } from '../api/admin.js';

export type StatDimension = 'kind' | 'provider';

export interface DimensionBucket {
  /** Nombre de la categoría (kind o provider). */
  key: string;
  /** Sumatoria en nano-USD. */
  costNano: bigint;
  /** Sumatoria en ms. */
  durationMs: number;
  /** Cantidad de spans que cayeron en este bucket. */
  spanCount: number;
}

/**
 * Spans que se excluyen de bucketing y totales.
 *
 * - `session`/`turn`/`composite`: contenedores cuyo `cost_nano` es
 *   `sum(children)` — sumarlos junto a los hijos double-cuenta.
 * - `job`: trabajos de post-procesamiento (destil, memory-consolidate, etc.)
 *   que corren después de cerrar la sesión. Mismo criterio que usa el backend
 *   en `/admin/sessions` (`COST_FILTER` en `apps/backend/src/routes/admin/sessions.ts`),
 *   así el comparador y los charts coinciden con la lista y el header.
 */
const CONTAINER_KINDS = new Set<string>(['session', 'turn', 'composite', 'job']);

function toBigIntSafe(v: string | number | null | undefined): bigint {
  if (v === null || v === undefined) return 0n;
  try {
    if (typeof v === 'number') {
      if (!Number.isFinite(v)) return 0n;
      return BigInt(Math.trunc(v));
    }
    const trimmed = v.trim();
    if (trimmed === '') return 0n;
    return BigInt(trimmed);
  } catch {
    return 0n;
  }
}

function providerOf(span: TimelineSpan): string {
  const provider = span.attributes?.['provider'];
  if (typeof provider === 'string' && provider.trim()) return provider;
  return 'unknown';
}

/**
 * Suma raw cost de TODOS los leaf spans (no-container) — la misma lógica
 * exacta que `aggregateByDimension`. Útil para el header del SessionDetail
 * que debe coincidir con el donut. Antes el header leía
 * `voice_sessions.total_cost_nano` (que excluye distill/jobs) y el donut
 * sumaba spans (que sí incluye distill) → mismas sesiones mostraban dos
 * totales distintos. Esta función es la fuente única para "total de la
 * sesión, incluyendo distill y jobs auxiliares, sin markup".
 */
export function totalCostFromSpans(spans: ReadonlyArray<TimelineSpan>): bigint {
  let total = 0n;
  for (const span of spans) {
    if (CONTAINER_KINDS.has(span.kind)) continue;
    total += toBigIntSafe(span.providerCostNano ?? span.costNano);
  }
  return total;
}

export function aggregateByDimension(
  spans: ReadonlyArray<TimelineSpan>,
  dim: StatDimension,
): DimensionBucket[] {
  const buckets = new Map<string, DimensionBucket>();
  for (const span of spans) {
    if (CONTAINER_KINDS.has(span.kind)) continue;
    const key = dim === 'kind' ? span.kind : providerOf(span);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { key, costNano: 0n, durationMs: 0, spanCount: 0 };
      buckets.set(key, bucket);
    }
    // Fase 34: vistas operativas del BO usan raw cost por default. Fallback
     // a `costNano` (con markup) cuando el span no tenga raw — backfill +
     // setCostBreakdown deberían cubrir todos los caminos, pero defensa.
    bucket.costNano += toBigIntSafe(span.providerCostNano ?? span.costNano);
    bucket.durationMs += Number.isFinite(span.durationMs) ? span.durationMs : 0;
    bucket.spanCount += 1;
  }
  return Array.from(buckets.values()).sort((a, b) => {
    // Ordenar por costo descendente; si empata costo, por duración.
    if (a.costNano !== b.costNano) return a.costNano > b.costNano ? -1 : 1;
    return b.durationMs - a.durationMs;
  });
}

export interface TurnSummary {
  turnId: string;
  turnIndex: number;
  startedAt: string;
  durationMs: number;
  costNano: bigint;
  errorCount: number;
  /** Spans del turn (incluye container turn + hijos). */
  spanIds: string[];
  /** Latency breakdown por kind, en ms. */
  latency: {
    stt: number;
    llm: number;
    tts: number;
  };
  userText: string | null;
  assistantText: string | null;
}

/**
 * Devuelve un resumen por turn agrupando spans cuyo ancestro raíz es un span
 * `kind === 'turn'`. Los spans sin turn ancestor quedan fuera (típicamente
 * spans de boot / control de la sesión).
 */
export function summarizeTurns(spans: ReadonlyArray<TimelineSpan>): TurnSummary[] {
  const byId = new Map<string, TimelineSpan>();
  for (const s of spans) byId.set(s.id, s);

  function findTurnAncestor(span: TimelineSpan): TimelineSpan | null {
    let current: TimelineSpan | undefined = span;
    let depth = 0;
    while (current && depth < 64) {
      if (current.kind === 'turn') return current;
      if (!current.parentId) return null;
      current = byId.get(current.parentId);
      depth += 1;
    }
    return null;
  }

  const turnSpans = new Map<string, TimelineSpan>();
  const childrenByTurn = new Map<string, TimelineSpan[]>();
  for (const span of spans) {
    if (span.kind === 'turn') {
      turnSpans.set(span.id, span);
      if (!childrenByTurn.has(span.id)) childrenByTurn.set(span.id, []);
      continue;
    }
    const turn = findTurnAncestor(span);
    if (!turn) continue;
    if (!childrenByTurn.has(turn.id)) childrenByTurn.set(turn.id, []);
    childrenByTurn.get(turn.id)!.push(span);
  }

  const summaries: TurnSummary[] = [];
  for (const [turnId, turnSpan] of turnSpans) {
    const children = childrenByTurn.get(turnId) ?? [];
    let costNano = 0n;
    let errorCount = 0;
    let sttMs = 0;
    let llmMs = 0;
    let ttsMs = 0;
    const spanIds: string[] = [turnId];
    for (const c of children) {
      costNano += toBigIntSafe(c.providerCostNano ?? c.costNano);
      if (c.isError) errorCount += 1;
      spanIds.push(c.id);
      if (c.kind === 'stt') sttMs += c.durationMs;
      else if (c.kind === 'llm') llmMs += c.durationMs;
      else if (c.kind === 'tts') ttsMs += c.durationMs;
    }
    const idxAttr = turnSpan.attributes?.['turn_index'] ?? turnSpan.attributes?.['turnIndex'];
    const turnIndex =
      typeof idxAttr === 'number'
        ? idxAttr
        : typeof idxAttr === 'string' && /^\d+$/.test(idxAttr)
          ? Number(idxAttr)
          : summaries.length;
    const userTextAttr = turnSpan.attributes?.['user_text'] ?? turnSpan.attributes?.['userText'];
    const botTextAttr =
      turnSpan.attributes?.['assistant_text'] ?? turnSpan.attributes?.['assistantText'];
    summaries.push({
      turnId,
      turnIndex,
      startedAt: turnSpan.startedAt,
      durationMs: turnSpan.durationMs,
      costNano: costNano + toBigIntSafe(turnSpan.providerCostNano ?? turnSpan.costNano),
      errorCount: errorCount + (turnSpan.isError ? 1 : 0),
      spanIds,
      latency: { stt: sttMs, llm: llmMs, tts: ttsMs },
      userText: typeof userTextAttr === 'string' ? userTextAttr : null,
      assistantText: typeof botTextAttr === 'string' ? botTextAttr : null,
    });
  }
  summaries.sort((a, b) => {
    if (a.turnIndex !== b.turnIndex) return a.turnIndex - b.turnIndex;
    return a.startedAt.localeCompare(b.startedAt);
  });
  return summaries;
}

export interface SessionAggregates {
  totalCostNano: bigint;
  totalDurationMs: number;
  spanCount: number;
  errorCount: number;
}

export type SpanOrigin = 'turns' | 'distill' | 'analyze' | 'other';

export const SPAN_ORIGIN_LABEL: Record<SpanOrigin, string> = {
  turns: 'Turnos',
  distill: 'Distill',
  analyze: 'Analyze',
  other: 'Otros',
};

export interface OriginBucket {
  origin: SpanOrigin;
  /** Nombre granular: para turns es 'turns', para jobs es el name del job root (ej 'session.distill.run'). */
  detailKey: string;
  costNano: bigint;
  spanCount: number;
  /** Breakdown por kind del span leaf (llm/tts/stt/…) dentro de este bucket. */
  byKind: Map<string, bigint>;
}

function originForRoot(rootKind: string, rootName: string): {
  origin: SpanOrigin;
  detailKey: string;
} {
  if (rootKind === 'turn') return { origin: 'turns', detailKey: 'turns' };
  if (rootKind === 'job') {
    const name = rootName.toLowerCase();
    if (name.includes('distill')) return { origin: 'distill', detailKey: rootName };
    if (name.includes('analyze')) return { origin: 'analyze', detailKey: rootName };
    return { origin: 'other', detailKey: rootName };
  }
  return { origin: 'other', detailKey: rootKind };
}

/**
 * Bucketiza cada leaf span por su raíz "lógica" (turn vs job vs otro), sumando
 * `providerCostNano ?? costNano` y desglosando por `kind` dentro del bucket.
 *
 * Walk del parent chain: para cada leaf no-container subimos hasta encontrar
 * un ancestro con kind `turn` o `job`. Si nunca aparece (typical: spans de
 * boot bajo `session`), cae a 'other'.
 *
 * Mismo criterio de exclusión que `aggregateByDimension` para los kinds
 * container (no double-cuentan sus propios costos).
 */
export function aggregateByOrigin(
  spans: ReadonlyArray<TimelineSpan>,
): OriginBucket[] {
  const byId = new Map<string, TimelineSpan>();
  for (const s of spans) byId.set(s.id, s);

  const findRoot = (span: TimelineSpan): TimelineSpan | null => {
    let current: TimelineSpan | undefined = span;
    let depth = 0;
    while (current && depth < 64) {
      if (current.kind === 'turn' || current.kind === 'job') return current;
      if (!current.parentId) return null;
      current = byId.get(current.parentId);
      depth += 1;
    }
    return null;
  };

  const buckets = new Map<SpanOrigin, OriginBucket>();
  const ensure = (origin: SpanOrigin, detailKey: string): OriginBucket => {
    const existing = buckets.get(origin);
    if (existing) return existing;
    const fresh: OriginBucket = {
      origin,
      detailKey,
      costNano: 0n,
      spanCount: 0,
      byKind: new Map<string, bigint>(),
    };
    buckets.set(origin, fresh);
    return fresh;
  };

  for (const span of spans) {
    if (CONTAINER_KINDS.has(span.kind)) continue;
    const root = findRoot(span);
    const { origin, detailKey } = root
      ? originForRoot(root.kind, root.name)
      : { origin: 'other' as SpanOrigin, detailKey: 'other' };
    const bucket = ensure(origin, detailKey);
    const cost = toBigIntSafe(span.providerCostNano ?? span.costNano);
    bucket.costNano += cost;
    bucket.spanCount += 1;
    bucket.byKind.set(span.kind, (bucket.byKind.get(span.kind) ?? 0n) + cost);
  }

  return Array.from(buckets.values()).sort((a, b) => {
    if (a.costNano !== b.costNano) return a.costNano > b.costNano ? -1 : 1;
    return b.spanCount - a.spanCount;
  });
}

export interface GranularOriginRow {
  origin: SpanOrigin;
  /** Nombre del root (job name o 'turns'). */
  rootName: string;
  costNano: bigint;
  spanCount: number;
  byKind: Map<string, bigint>;
}

/**
 * Versión más granular: cada job root es su propio bucket (en lugar de
 * colapsar todos los jobs de tipo "distill" o "analyze" juntos), y los spans
 * sin raíz turn/job se desagregan por su propio `name` en vez de caer en un
 * genérico "other". Útil para la vista expandida del donut, donde el usuario
 * quiere ver de qué job o llamada suelta vino cada costo.
 */
export function aggregateGranularByOrigin(
  spans: ReadonlyArray<TimelineSpan>,
): GranularOriginRow[] {
  const byId = new Map<string, TimelineSpan>();
  for (const s of spans) byId.set(s.id, s);

  const findRoot = (span: TimelineSpan): TimelineSpan | null => {
    let current: TimelineSpan | undefined = span;
    let depth = 0;
    while (current && depth < 64) {
      if (current.kind === 'turn' || current.kind === 'job') return current;
      if (!current.parentId) return null;
      current = byId.get(current.parentId);
      depth += 1;
    }
    return null;
  };

  const rows = new Map<string, GranularOriginRow>();
  const ensure = (origin: SpanOrigin, rootName: string): GranularOriginRow => {
    const key = `${origin}::${rootName}`;
    const existing = rows.get(key);
    if (existing) return existing;
    const fresh: GranularOriginRow = {
      origin,
      rootName,
      costNano: 0n,
      spanCount: 0,
      byKind: new Map<string, bigint>(),
    };
    rows.set(key, fresh);
    return fresh;
  };

  for (const span of spans) {
    if (CONTAINER_KINDS.has(span.kind)) continue;
    const root = findRoot(span);
    let origin: SpanOrigin;
    let rootName: string;
    if (!root) {
      // Span sin raíz turn/job: típicamente una llamada LLM suelta que corrió
      // fuera del árbol de un turno (ej. 'lookup', 'suggest',
      // 'anthropic.translate'). En la vista granular NO lo colapsamos en un
      // genérico 'other' — usamos su propio `name` para que el usuario vea de
      // qué llamada vino el costo. (El donut sí los agrupa todos bajo "Otros".)
      origin = 'other';
      rootName = span.name;
    } else if (root.kind === 'turn') {
      origin = 'turns';
      rootName = 'turns';
    } else {
      const info = originForRoot(root.kind, root.name);
      origin = info.origin;
      rootName = root.name;
    }
    const row = ensure(origin, rootName);
    const cost = toBigIntSafe(span.providerCostNano ?? span.costNano);
    row.costNano += cost;
    row.spanCount += 1;
    row.byKind.set(span.kind, (row.byKind.get(span.kind) ?? 0n) + cost);
  }

  return Array.from(rows.values()).sort((a, b) => {
    if (a.costNano !== b.costNano) return a.costNano > b.costNano ? -1 : 1;
    return b.spanCount - a.spanCount;
  });
}

export function aggregateTotals(spans: ReadonlyArray<TimelineSpan>): SessionAggregates {
  let totalCostNano = 0n;
  let totalDurationMs = 0;
  let errorCount = 0;
  for (const s of spans) {
    if (CONTAINER_KINDS.has(s.kind)) continue;
    totalCostNano += toBigIntSafe(s.providerCostNano ?? s.costNano);
    totalDurationMs += Number.isFinite(s.durationMs) ? s.durationMs : 0;
    if (s.isError) errorCount += 1;
  }
  return { totalCostNano, totalDurationMs, spanCount: spans.length, errorCount };
}
